import { logRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/tikal-casino/log-rocket-api-key';
import { registerLogRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/token';
import { domainNumberStrExtractor } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/domain-number-extractor';
import { registerDiToken } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import type { DeviceInfoPayload } from '@bcf-vanilla-ts-v1-utilities/device-info/types';
import { extractDevice } from '@bcf-vanilla-ts-v1-utilities/device-info/utils';
import { registerPwaDeviceContext } from '@bcf-vanilla-ui-ts-v1/shared/pwa/pwa-device-context';
import { registerEnvBase } from '@bcf2-envs/env-token';
import { appMobileCasinoCountryBlocker } from '@bcf2-v1-configurators-tokens/app-country-blocker/tikal-casino/app-country-blocker';
import { APP_COUNTRY_BLOCKER } from '@bcf2-v1-configurators-tokens/app-country-blocker/token';
import { APP_RELEASE_VERSION } from '@bcf2-v1-configurators-tokens/app/token';
import { casinoLink } from '@bcf2-v1-configurators-tokens/casino-links/tikal-casino/casino-links';
import { CASINO_FULL_URL } from '@bcf2-v1-configurators-tokens/casino-links/token';
import { defaultLangRedirectorCustomCountryLangMap } from '@bcf2-v1-configurators-tokens/default-lang-redirector/tikal-casino/default-lang-redirector-custom-country-lang.map';
import { DEFAULT_LANG_REDIRECTOR_CUSTOM_COUNTRY_LANG_MAP } from '@bcf2-v1-configurators-tokens/default-lang-redirector/token';
import { FOOTER_LICENSE_CUSTOM_COMPONENT } from '@bcf2-v1-configurators-tokens/footer-license/token';
import { sportsbookFooterLinks } from '@bcf2-v1-configurators-tokens/footer-links/tikal-casino/footer-links';
import { FOOTER_LINKS } from '@bcf2-v1-configurators-tokens/footer-links/token';
import { imgWebConfig } from '@bcf2-v1-configurators-tokens/img-webp/tikal-casino/img-web-config';
import { IMG_WEB_CONFIG } from '@bcf2-v1-configurators-tokens/img-webp/token';
import { sportsMobileOptiLitswRegistrationConfig } from '@bcf2-v1-configurators-tokens/litsw-registration/litsw-registration-presets';
import { LITSW_REGISTRATION_CONFIG } from '@bcf2-v1-configurators-tokens/litsw-registration/token';
import {
  OPTIMOVE_SDK_CONFIG_LOADER,
  OPTIMOVE_WRAPPER_STYLES_LOADER
} from '@bcf2-v1-configurators-tokens/optimove-sdk/tokens';
import { OptimoveSdkConfigs } from '@bcf2-v1-configurators-tokens/optimove-sdk/types';
import { piqTheme } from '@bcf2-v1-configurators-tokens/piq-styles/janusz/piq-theme';
import { piqStylesMobile } from '@bcf2-v1-configurators-tokens/piq-styles/janusz/piq.mobile.styles';
import { PIQ_CSS, PIQ_THEME } from '@bcf2-v1-configurators-tokens/piq-styles/token';
import { wizardPostFormHiddenFieldsWithoutPromocode } from '@bcf2-v1-configurators-tokens/wizard-post/curacao/wizard-form-post-hidden-fields';
import { cpWizardPostFormLayoutConfig } from '@bcf2-v1-configurators-tokens/wizard-post/curacao/wizard-post-form-layout-config';
import {
  CP_WIZARD_POST_FORM_LAYOUT_CONFIG,
  WIZARD_POST_FORM_HIDDEN_FIELDS
} from '@bcf2-v1-configurators-tokens/wizard-post/token';
import { configureSvgIcons } from '@bcfds-design-system/components/svg-icon/configurator';
import { envCommonToLit20EnvBase } from '@bcflit-env/env-converter';
import { EnvCommon } from '@bcflit-env/types';
import { releaseVersion } from '@bcflit-release-version';
import { APP_WITH_WORKER } from '@bcflit-v1-configurators/tokens/app/token';
import { USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE } from '@bcflit-v1-configurators/tokens/date-format/token';
import { CSSResultGroup, TemplateResult } from 'lit';

export function registerSettingsProviders(envCommon: EnvCommon): void {
  registerDiToken(CASINO_FULL_URL, () => casinoLink);
  registerDiToken(APP_RELEASE_VERSION, () => releaseVersion);
  registerEnvBase(envCommonToLit20EnvBase(envCommon));
  const deviceInfo: DeviceInfoPayload = extractDevice('mobile-web', window, navigator);
  registerPwaDeviceContext({
    isMobile: () => deviceInfo.isMobile,
    isPWA: () => deviceInfo.isPwa,
    isIosDevice: () => deviceInfo.isIos,
    isSafari: () => deviceInfo.isSafari
  });
  registerDiToken(DEFAULT_LANG_REDIRECTOR_CUSTOM_COUNTRY_LANG_MAP, () => defaultLangRedirectorCustomCountryLangMap);
  registerDiToken(IMG_WEB_CONFIG, () => imgWebConfig);
  registerDiToken(CP_WIZARD_POST_FORM_LAYOUT_CONFIG, () => cpWizardPostFormLayoutConfig);
  registerDiToken(WIZARD_POST_FORM_HIDDEN_FIELDS, () => wizardPostFormHiddenFieldsWithoutPromocode);
  registerDiToken(APP_WITH_WORKER, () => true);
  registerDiToken(PIQ_THEME, () => piqTheme);
  registerDiToken(PIQ_CSS, () => async (): Promise<string> => piqStylesMobile);
  configureSvgIcons({
    domainNumberStr: domainNumberStrExtractor(window.location.hostname),
    svgIconConfig: envCommon.svgIconConfig
  });
  registerDiToken(APP_COUNTRY_BLOCKER, () => appMobileCasinoCountryBlocker);
  registerDiToken(FOOTER_LINKS, () => sportsbookFooterLinks);
  registerDiToken(USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE, () => true);
  registerLogRocketApiKey(logRocketApiKey);
  registerDiToken(
    FOOTER_LICENSE_CUSTOM_COMPONENT,
    () => (): Promise<TemplateResult> =>
      // eslint-disable-next-line @typescript-eslint/typedef
      import('@bcf2-v1-configurators-tokens/footer-license/tikal-casino/footer-license').then((mod) =>
        mod.tikalCasinoFooterLicense()
      )
  );
  registerDiToken(LITSW_REGISTRATION_CONFIG, () => sportsMobileOptiLitswRegistrationConfig);
  registerDiToken(
    OPTIMOVE_WRAPPER_STYLES_LOADER,
    () => (): Promise<CSSResultGroup> =>
      import('@bcf2-v1-configurators-tokens/optimove-sdk/tikal-casino/optimove-wrapper.mobile.styles').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveWrapperMobileStyles
      )
  );
  registerDiToken(
    OPTIMOVE_SDK_CONFIG_LOADER,
    () => (): Promise<OptimoveSdkConfigs> =>
      import('@bcf2-v1-configurators-tokens/optimove-sdk/tikal-casino/sportsbook/mobile-web/optimove-sdk-config').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveSdkConfigs
      )
  );
}
